import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import styles from "./ApiReference.module.css";

/**
 * Reference for the real HTTP API served behind API Gateway at /api/*.
 *
 * Every endpoint below is taken directly from the request router in
 * infra/lambdas/api/index.ts — no endpoint, method, or path is invented.
 * Auth column reflects how the router actually gates each route:
 *   - "Public"  → reachable without a token (only /api/health + /health).
 *   - "Bearer"  → verifyAuth() must succeed (Cognito ID token in the
 *                 `Authorization: Bearer <jwt>` header) before dispatch.
 *   - "Admin"   → Bearer + the handler additionally requires the caller to
 *                 be in the `sm-admins` Cognito group (403 otherwise).
 */

type Auth = "Public" | "Bearer" | "Admin";

interface Endpoint {
  method: "GET" | "POST" | "PUT";
  path: string;
  purpose: string;
  auth: Auth;
}

interface Group {
  id: string;
  title: string;
  blurb: string;
  endpoints: Endpoint[];
}

const GROUPS: Group[] = [
  {
    id: "system",
    title: "System",
    blurb: "Health checks. The only routes that do not require a token.",
    endpoints: [
      {
        method: "GET",
        path: "/api/health",
        purpose: "Liveness check. Returns stage, region, table name, and time.",
        auth: "Public",
      },
      {
        method: "GET",
        path: "/health",
        purpose: "Alias of /api/health (same handler).",
        auth: "Public",
      },
    ],
  },
  {
    id: "auth",
    title: "Auth",
    blurb: "Identity of the signed-in caller.",
    endpoints: [
      {
        method: "GET",
        path: "/api/me",
        purpose: "Returns the caller's sub, email, verification, groups, and admin flag.",
        auth: "Bearer",
      },
    ],
  },
  {
    id: "workspaces",
    title: "Workspaces",
    blurb: "List, create, and read workspaces the caller belongs to.",
    endpoints: [
      {
        method: "GET",
        path: "/api/workspaces",
        purpose: "List the workspaces the caller is a member of.",
        auth: "Bearer",
      },
      {
        method: "POST",
        path: "/api/workspaces",
        purpose: "Create a workspace (writes the workspace + owner membership).",
        auth: "Bearer",
      },
      {
        method: "GET",
        path: "/api/workspaces/{ws}",
        purpose: "Read a single workspace.",
        auth: "Bearer",
      },
    ],
  },
  {
    id: "brands",
    title: "Brands",
    blurb: "Brands live under a workspace. The brand body is the brand brain.",
    endpoints: [
      {
        method: "GET",
        path: "/api/workspaces/{ws}/brands",
        purpose: "List brands in the workspace.",
        auth: "Bearer",
      },
      {
        method: "POST",
        path: "/api/workspaces/{ws}/brands",
        purpose: "Create a brand in the workspace.",
        auth: "Bearer",
      },
      {
        method: "GET",
        path: "/api/workspaces/{ws}/brands/{brand}",
        purpose: "Read a single brand (its brand brain).",
        auth: "Bearer",
      },
      {
        method: "PUT",
        path: "/api/workspaces/{ws}/brands/{brand}",
        purpose: "Update a brand's brain. Requires the manageBrands permission.",
        auth: "Bearer",
      },
    ],
  },
  {
    id: "drafts",
    title: "Drafts",
    blurb:
      "Generate, list, and act on drafts under a brand. Generation is async (returns a job id); poll the job until done.",
    endpoints: [
      {
        method: "GET",
        path: "/api/workspaces/{ws}/brands/{brand}/drafts",
        purpose: "List drafts for the brand.",
        auth: "Bearer",
      },
      {
        method: "POST",
        path: "/api/workspaces/{ws}/brands/{brand}/drafts",
        purpose: "Enqueue a generation job ({input, platforms, kinds, providers}); returns 202 with a jobId.",
        auth: "Bearer",
      },
      {
        method: "GET",
        path: "/api/workspaces/{ws}/brands/{brand}/jobs/{jobId}",
        purpose: "Poll a generation job's status (queued / running / done / failed).",
        auth: "Bearer",
      },
      {
        method: "POST",
        path: "/api/workspaces/{ws}/brands/{brand}/drafts/{draft}/approve",
        purpose:
          "Approve a draft. A blocking safety-rail warning is refused unless override + reason are sent.",
        auth: "Bearer",
      },
      {
        method: "POST",
        path: "/api/workspaces/{ws}/brands/{brand}/drafts/{draft}/reject",
        purpose: "Reject a draft with an optional reason.",
        auth: "Bearer",
      },
      {
        method: "POST",
        path: "/api/workspaces/{ws}/brands/{brand}/drafts/{draft}/schedule",
        purpose: "Schedule an already-approved draft for a future time (linear flow).",
        auth: "Bearer",
      },
      {
        method: "POST",
        path: "/api/workspaces/{ws}/brands/{brand}/drafts/{draft}/edit",
        purpose: "Edit a draft's variant text; re-validates and returns it to pending.",
        auth: "Bearer",
      },
    ],
  },
  {
    id: "scan",
    title: "Scan",
    blurb:
      "Two separate endpoints: kick off a brand-learning scan, then poll it. The poll route is /scan-jobs/{id}, distinct from POST /scan.",
    endpoints: [
      {
        method: "POST",
        path: "/api/workspaces/{ws}/brands/{brand}/scan",
        purpose: "Start a brand-learning scan job for the brand.",
        auth: "Bearer",
      },
      {
        method: "GET",
        path: "/api/workspaces/{ws}/brands/{brand}/scan-jobs/{id}",
        purpose: "Poll a scan job by its id.",
        auth: "Bearer",
      },
    ],
  },
  {
    id: "comments",
    title: "Comments",
    blurb: "The review-thread comments on a draft.",
    endpoints: [
      {
        method: "GET",
        path: "/api/workspaces/{ws}/brands/{brand}/drafts/{draft}/comments",
        purpose: "List the comment thread on a draft.",
        auth: "Bearer",
      },
      {
        method: "POST",
        path: "/api/workspaces/{ws}/brands/{brand}/drafts/{draft}/comments",
        purpose: "Post a comment to a draft's thread (non-viewer roles only).",
        auth: "Bearer",
      },
    ],
  },
  {
    id: "admin",
    title: "Admin",
    blurb:
      "Platform-admin only. Each handler re-checks the sm-admins group and returns 403 if the caller is not an admin.",
    endpoints: [
      {
        method: "GET",
        path: "/api/admin/waitlist",
        purpose: "List confirmed-but-disabled users waiting for approval.",
        auth: "Admin",
      },
      {
        method: "POST",
        path: "/api/admin/waitlist/approve",
        purpose: "Approve a waitlisted user ({username}); re-enables their Cognito account.",
        auth: "Admin",
      },
      {
        method: "POST",
        path: "/api/admin/waitlist/reject",
        purpose: "Reject a waitlisted user ({username}); deletes their Cognito account.",
        auth: "Admin",
      },
    ],
  },
];

function MethodPill({ method }: { method: Endpoint["method"] }) {
  const cls =
    method === "GET"
      ? styles.mGet
      : method === "POST"
        ? styles.mPost
        : styles.mPut;
  return <span className={`${styles.method} ${cls}`}>{method}</span>;
}

function AuthPill({ auth }: { auth: Auth }) {
  const cls =
    auth === "Public" ? styles.aPublic : auth === "Admin" ? styles.aAdmin : styles.aBearer;
  return <span className={`${styles.authPill} ${cls}`}>{auth}</span>;
}

export function ApiReference() {
  return (
    <>
      <MarketingNav />

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.kicker}>API Reference</p>
            <h1 className={styles.title}>The HTTP API</h1>
            <p className={styles.lead}>
              All endpoints are served behind API Gateway at{" "}
              <code>/api/*</code>. Except the health checks, every route
              requires a Cognito ID token in an{" "}
              <code>Authorization: Bearer &lt;jwt&gt;</code> header; admin routes
              additionally require the <code>sm-admins</code> group.
            </p>
          </div>
        </section>

        <div className={styles.body}>
          <nav className={styles.sidebar} aria-label="API groups">
            <ul className={styles.navList}>
              {GROUPS.map((g) => (
                <li key={g.id}>
                  <a className={styles.navLink} href={`#${g.id}`}>
                    {g.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className={styles.content}>
            {GROUPS.map((group) => (
              <section id={group.id} className={styles.group} key={group.id}>
                <h2 className={styles.h2}>{group.title}</h2>
                <p className={styles.groupBlurb}>{group.blurb}</p>
                <div className={styles.endpoints}>
                  {group.endpoints.map((ep) => (
                    <div className={styles.endpoint} key={`${ep.method} ${ep.path}`}>
                      <div className={styles.epHead}>
                        <MethodPill method={ep.method} />
                        <code className={styles.epPath}>{ep.path}</code>
                        <AuthPill auth={ep.auth} />
                      </div>
                      <p className={styles.epPurpose}>{ep.purpose}</p>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>

      <MarketingFooter />
    </>
  );
}

export default ApiReference;
